import type { HttpParamsOption } from './api-gateway.models';

/**
 * Strategy to use when converting an object to search params.
 * - COMMA_SEPARATED: Convert arrays to a comma-separated string.
 * - MULTIPLE_PARAMS: Convert arrays to multiple parameters with the same key.
 */
export enum ArrayStrategy {
  COMMA_SEPARATED = 'comma_separated',
  MULTIPLE_PARAMS = 'multiple_params',
}

function isJsObject(o: unknown): boolean {
  return o !== null && (typeof o === 'function' || typeof o === 'object') && !Array.isArray(o);
}

function camelCaseToSnakeCase(key: string): string {
  return key
    .replace(/([A-Z])/g, '_$1')
    .replace(/^_/, '')
    .toLowerCase();
}

function camelizeObjectKeys(object: object, arrayStrategy?: ArrayStrategy): HttpParamsOption {
  return Object.entries(object).reduce<HttpParamsOption>((acc, [key, value]) => {
    if (value === undefined || value === null) {
      return acc;
    }

    const snakeKey = camelCaseToSnakeCase(key);
    const isArray = Array.isArray(value) && value.length > 0;
    if (isArray && arrayStrategy !== undefined) {
      switch (arrayStrategy) {
        case ArrayStrategy.COMMA_SEPARATED:
          acc[snakeKey] = value.join(',');
          break;
        case ArrayStrategy.MULTIPLE_PARAMS:
          value.forEach((v, i) => {
            acc[`${snakeKey}[${i}]`] = v;
          });
          break;
      }
    } else if (value) {
      acc[snakeKey] = isJsObject(value) ? camelizeObjectKeys(value, arrayStrategy) : value;
    }

    return acc;
  }, {});
}

/**
 * Converts a plain object to search params with camelCase keys converted to snake_case.
 * Returns a ParamsOption compatible with HttpClient request options.
 */
export function objectToSearchParams(
  params: object | null | undefined,
  arrayStrategy?: ArrayStrategy,
): HttpParamsOption {
  if (!params) {
    return {};
  }
  return camelizeObjectKeys(params, arrayStrategy);
}
